import { Suspense, lazy, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AppLayout } from './components/AppLayout';
import { ToastProvider } from './components/ui';
import { FullPageLoading, RouteFallback } from './components/feedback';
import { Field, FormActions, Select } from './components/forms';
import { Landing } from './pages/Landing';
import { AuthPage } from './pages/Auth';
import { ResetPasswordPage } from './pages/ResetPassword';

/* Route pages are code-split: each loads on first visit so the initial
   bundle stays small. Landing + auth stay eager (first paint / sign-in). */
const Analytics = lazy(() => import('./pages/Analytics').then((m) => ({ default: m.Analytics })));
const ValPage = lazy(() => import('./pages/ValPage').then((m) => ({ default: m.ValPage })));
const Billing = lazy(() => import('./pages/Billing').then((m) => ({ default: m.Billing })));
const Calendar = lazy(() => import('./pages/Calendar').then((m) => ({ default: m.Calendar })));
const Discover = lazy(() => import('./pages/Discover').then((m) => ({ default: m.Discover })));
const Documents = lazy(() => import('./pages/Documents').then((m) => ({ default: m.Documents })));
const Vault = lazy(() => import('./pages/Vault').then((m) => ({ default: m.Vault })));
const Registrations = lazy(() => import('./pages/Registrations').then((m) => ({ default: m.Registrations })));
const Opportunities = lazy(() => import('./pages/Opportunities').then((m) => ({ default: m.Opportunities })));
const OpportunityDetail = lazy(() => import('./pages/OpportunityDetail').then((m) => ({ default: m.OpportunityDetail })));
const OpportunityForm = lazy(() => import('./pages/OpportunityForm').then((m) => ({ default: m.OpportunityForm })));
const Overview = lazy(() => import('./pages/Overview').then((m) => ({ default: m.Overview })));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Pipeline = lazy(() => import('./pages/Pipeline').then((m) => ({ default: m.Pipeline })));
const Portfolio = lazy(() => import('./pages/Portfolio').then((m) => ({ default: m.Portfolio })));
const Pricing = lazy(() => import('./pages/Pricing').then((m) => ({ default: m.Pricing })));
const Privacy = lazy(() => import('./pages/Privacy').then((m) => ({ default: m.Privacy })));
const Profile = lazy(() => import('./pages/Profile').then((m) => ({ default: m.Profile })));
const Proposals = lazy(() => import('./pages/Proposals').then((m) => ({ default: m.Proposals })));
const Activity = lazy(() => import('./pages/Activity').then((m) => ({ default: m.Activity })));
const Analyze = lazy(() => import('./pages/Analyze').then((m) => ({ default: m.Analyze })));
const Awards = lazy(() => import('./pages/Awards').then((m) => ({ default: m.Awards })));
const AwardDetail = lazy(() => import('./pages/AwardDetail').then((m) => ({ default: m.AwardDetail })));
const ProposalWorkspace = lazy(() => import('./pages/ProposalWorkspace').then((m) => ({ default: m.ProposalWorkspace })));
const ProposalWorkspaceLoader = lazy(() => import('./pages/ProposalWorkspace').then((m) => ({ default: m.ProposalWorkspaceLoader })));
const Readiness = lazy(() => import('./pages/Readiness'));
const HowTo = lazy(() => import('./pages/HowTo'));
const GovConReadiness = lazy(() => import('./pages/GovConReadiness'));
const Saved = lazy(() => import('./pages/Saved').then((m) => ({ default: m.Saved })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const Stakeholders = lazy(() => import('./pages/Stakeholders').then((m) => ({ default: m.Stakeholders })));
const Relationships = lazy(() => import('./pages/Relationships').then((m) => ({ default: m.Relationships })));
const IndividualHome = lazy(() => import('./pages/individual/IndividualHome').then((m) => ({ default: m.IndividualHome })));
const IndividualOnboarding = lazy(() => import('./pages/individual/IndividualOnboarding').then((m) => ({ default: m.IndividualOnboarding })));
const IndividualDiscover = lazy(() => import('./pages/individual/IndividualDiscover').then((m) => ({ default: m.IndividualDiscover })));
const IndividualOpportunityDetail = lazy(() => import('./pages/individual/IndividualOpportunityDetail').then((m) => ({ default: m.IndividualOpportunityDetail })));
const IndividualApplications = lazy(() => import('./pages/individual/IndividualApplications').then((m) => ({ default: m.IndividualApplications })));
const IndividualApplicationDetail = lazy(() => import('./pages/individual/IndividualApplicationDetail').then((m) => ({ default: m.IndividualApplicationDetail })));
const IndividualProfilePage = lazy(() => import('./pages/individual/IndividualProfilePage').then((m) => ({ default: m.IndividualProfilePage })));
const Tasks = lazy(() => import('./pages/Tasks').then((m) => ({ default: m.Tasks })));
const Terms = lazy(() => import('./pages/Terms').then((m) => ({ default: m.Terms })));

function OrgPicker() {
  const { memberships, selectOrg } = useAuth();
  const [orgId, setOrgId] = useState(memberships[0]?.organizationId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setBusy(true);
    setError(null);
    try {
      await selectOrg(orgId);
    } catch {
      setError('Could not select that organization. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <h1>Choose your workspace</h1>
        <p className="auth-sub">
          You belong to multiple organizations. Pick one to continue.
        </p>
        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}
        <form onSubmit={submit}>
          <Field label="Organization" htmlFor="org-picker">
            <Select
              id="org-picker"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
            >
              {memberships.map((m) => (
                <option key={m.organizationId} value={m.organizationId}>
                  {m.organizationName} ({m.role})
                </option>
              ))}
            </Select>
          </Field>
          <FormActions>
            <button type="submit" className="btn btn-primary btn-block" disabled={busy || !orgId}>
              {busy ? 'Opening…' : 'Continue'}
            </button>
          </FormActions>
        </form>
      </div>
    </div>
  );
}

/** Individuals land on their own home and never see organization screens. */
function KindGate({ children }: { children: React.ReactNode }) {
  const { isIndividual, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoading label="Loading your workspace…" />;
  if (isIndividual && !location.pathname.startsWith('/app/individual')) {
    return <Navigate to="/app/individual" replace />;
  }
  return <>{children}</>;
}

/** Guards /app: requires a session, then requires a selected org. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, memberships, currentOrg } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoading label="Loading your workspace…" />;
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!currentOrg) {
    if (memberships.length === 0) {
      // Signed in but no org (shouldn't happen via normal flows).
      return (
        <div className="auth-wrap">
          <div className="auth-card">
            <h1>No organization</h1>
            <p className="auth-sub">
              Your account isn't part of any organization yet. Create a new
              workspace to get started.
            </p>
            <FormActions>
              <a className="btn btn-primary btn-block" href="/signup">
                Create workspace
              </a>
            </FormActions>
          </div>
        </div>
      );
    }
    return <OrgPicker />;
  }
  return <>{children}</>;
}

const ROUTE_TITLES: Array<[RegExp, string]> = [
  [/^\/app\/individual\/onboarding/, 'Personal funding setup'],
  [/^\/app\/individual\/discover\//, 'Program details'],
  [/^\/app\/individual\/discover/, 'Discover programs'],
  [/^\/app\/individual\/applications\//, 'Application'],
  [/^\/app\/individual\/applications/, 'My applications'],
  [/^\/app\/individual\/profile/, 'My profile'],
  [/^\/app\/individual/, 'Home'],
  [/^\/app\/overview/, 'Overview'],
  [/^\/app\/discover/, 'Discover'],
  [/^\/app\/analyze/, 'Analyze'],
  [/^\/app\/opportunities\/new/, 'Add opportunity'],
  [/^\/app\/opportunities\//, 'Opportunity'],
  [/^\/app\/saved/, 'Saved'],
  [/^\/app\/pipeline/, 'Pipeline'],
  [/^\/app\/portfolio/, 'Portfolio'],
  [/^\/app\/proposals/, 'Proposals'],
  [/^\/app\/awards\//, 'Award'],
  [/^\/app\/awards/, 'Awards'],
  [/^\/app\/readiness/, 'Readiness'],
  [/^\/app\/govcon-readiness/, 'GovCon Readiness'],
  [/^\/app\/onboarding/, 'Onboarding'],
  [/^\/app\/vault/, 'Document vault'],
  [/^\/app\/registrations/, 'Registrations'],
  [/^\/app\/activity/, 'Activity'],
  [/^\/app\/tasks/, 'Tasks'],
  [/^\/app\/calendar/, 'Calendar'],
  [/^\/app\/agents/, 'Ask Val…'],
  [/^\/app\/team/, 'Stakeholders'],
  [/^\/app\/relationships/, 'Relationships'],
  [/^\/app\/documents/, 'Documents'],
  [/^\/app\/reports/, 'Reports'],
  [/^\/app\/organization/, 'Organization'],
  [/^\/app\/billing/, 'Billing'],
  [/^\/app\/settings/, 'Settings'],
  [/^\/pricing/, 'Pricing'],
  [/^\/login/, 'Log in'],
  [/^\/signup/, 'Sign up'],
  [/^\/reset-password/, 'Reset password'],
  [/^\/privacy/, 'Privacy'],
  [/^\/terms/, 'Terms'],
];

/** Keep document.title in sync with the route (WCAG 2.4.2). */
function RouteTitle() {
  const location = useLocation();
  useEffect(() => {
    const match = ROUTE_TITLES.find(([re]) => re.test(location.pathname));
    document.title = match ? `${match[1]} — Grant & RFP Navigator` : 'Grant & RFP Navigator';
  }, [location.pathname]);
  return null;
}

/**
 * Scroll to the element matching the URL hash after navigation (e.g. /#how
 * from the Pricing page). React Router doesn't do this on its own.
 */
function HashScroll() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    // Let the new route paint before measuring.
    const id = window.setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 60);
    return () => window.clearTimeout(id);
  }, [pathname, hash]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <RouteTitle />
      <HashScroll />
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public marketing pages */}
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<AuthPage initialTab="login" />} />
      <Route path="/signup" element={<AuthPage initialTab="signup" />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* Protected app */}
      <Route
        path="/app"
        element={
          <RequireAuth>
            <KindGate>
              <AppLayout />
            </KindGate>
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/app/overview" replace />} />
        <Route path="overview" element={<Overview />} />
        <Route path="dashboard" element={<Navigate to="/app/overview" replace />} />
        <Route path="discover" element={<Discover />} />
        <Route path="analyze" element={<Analyze />} />
        <Route path="opportunities" element={<Navigate to="/app/discover" replace />} />
        <Route path="opportunities/new" element={<OpportunityForm />} />
        <Route path="opportunities/:id" element={<OpportunityDetail />} />
        <Route path="opportunities/:id/edit" element={<OpportunityForm />} />
        <Route path="saved" element={<Saved />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="proposals" element={<Proposals />} />
        <Route path="awards" element={<Awards />} />
        <Route path="awards/:id" element={<AwardDetail />} />
        <Route path="readiness" element={<Readiness />} />
        <Route path="how-to" element={<HowTo />} />
        <Route path="govcon-readiness" element={<GovConReadiness />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="vault" element={<Vault />} />
        <Route path="registrations" element={<Registrations />} />
        <Route path="activity" element={<Activity />} />
        <Route path="proposals/opp/:opportunityId" element={<ProposalWorkspaceLoader />} />
        <Route path="proposals/:id" element={<ProposalWorkspace />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="agents" element={<ValPage />} />
        <Route path="val" element={<Navigate to="/app/agents" replace />} />
        <Route path="agents/:agentId" element={<ValPage />} />
        <Route path="team" element={<Stakeholders />} />
        <Route path="relationships" element={<Relationships />} />
        <Route path="stakeholders" element={<Navigate to="/app/team" replace />} />
        <Route path="documents" element={<Documents />} />
        <Route path="reports" element={<Analytics />} />
        <Route path="analytics" element={<Navigate to="/app/reports" replace />} />
        <Route path="organization" element={<Profile />} />
        <Route path="profile" element={<Navigate to="/app/organization" replace />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />

        {/* Individual workflow */}
        <Route path="individual" element={<IndividualHome />} />
        <Route path="individual/onboarding" element={<IndividualOnboarding />} />
        <Route path="individual/discover" element={<IndividualDiscover />} />
        <Route path="individual/discover/:id" element={<IndividualOpportunityDetail />} />
        <Route path="individual/applications" element={<IndividualApplications />} />
        <Route path="individual/applications/:id" element={<IndividualApplicationDetail />} />
        <Route path="individual/profile" element={<IndividualProfilePage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
